/**
 * 回の帯（036、docs/27 §3。#188 で作り直し＝docs/27 §7、Figma 173:99。#190 で1段に固定＝docs/27 §7.10）。見積の画面の上に、1回目 → 2回目 → 3回目 と並べる。
 *   ・左右に分ける。左＝回のチップ（1段のまま横に流す＝折り返さない。開いたとき表示中の回が見える位置へ。回が3つ以上なら左右に ‹ ›）。
 *     右＝幅の決まったブロック（訂正中だけ「相違 なし／表示／変更行のみ」と件数のバッジ＝Estimate.tsx が marks で渡す）→「比較」→ 畳む ⌃
 *   ・幅が足りないときは右のブロックだけ2段目へ（チップは折り返さない）
 *   ・回ごとに 状態（下書き／発行済／前の回）・日付・合計・前の回からの増減。回を押すと、その回と前の回の「見積書の比較」へ。前の回には控えの PDF
 *   ・畳むと1行 44px：回は文字だけ「1回目 10/11 698,500円 → 2回目 下書き 1,358,500円（＋660,000）」、相違のセグメントと「比較」はそのまま（件数は出さない）。
 *     畳んだかは端末に覚える（localStorage gb.est.rounds）。待つ間の形（RoundStripLoading）も同じ高さ（#179）
 *   ・2回目が無い見積・履歴と差分の無いプランでは出さない（呼ぶ側が決める）
 *   ・{order}（#233 2b）も同じ部品：doc＝"orders"（道の頭・控えの鍵）・label＝帯の名前・previousExtra＝前の回の承諾の記録（{orderAck}・請書の写し）
 */
import { MSG, ROUND_MSG, ROUND_STATE_LABEL, type RevisionRound, type RevisionRounds, type RoundState } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { api } from "../api.js";
import { mdSlash, yen } from "../lib/num.js";
import { usePdfTab } from "../lib/pdfOpen.js";

const COLLAPSE_KEY = "gb.est.rounds";
/** 畳んだか（この端末に覚える。読めない端末では広げたまま） */
function useCollapsed(): [boolean, (v: boolean) => void] {
  const [v, setV] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "closed";
    } catch {
      return false;
    }
  });
  return [
    v,
    (on: boolean) => {
      setV(on);
      try {
        if (on) localStorage.setItem(COLLAPSE_KEY, "closed");
        else localStorage.removeItem(COLLAPSE_KEY);
      } catch {
        /* 控えられない端末では、この画面の間だけ */
      }
    },
  ];
}

/** 前の回からの増減（0 は出さない。−は朱） */
function DeltaText({ d }: { d: number | null }) {
  if (d == null || d === 0) return null;
  return <span className={"num" + (d < 0 ? " shu" : "")}>（{d > 0 ? "＋" : "−"}{yen(Math.abs(d))}）</span>;
}

/** 畳む ⌃／広げる ⌄ */
function Toggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  return (
    <button type="button" className="rtoggle" aria-label={collapsed ? ROUND_MSG.expand : ROUND_MSG.collapse} title={collapsed ? ROUND_MSG.expand : ROUND_MSG.collapse} aria-expanded={!collapsed} onClick={onToggle} data-testid="rounds-toggle">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d={collapsed ? "M6 9l6 6 6-6" : "M6 15l6-6 6 6"} />
      </svg>
    </button>
  );
}

/** 右のブロックに入れる相違の操作（訂正中だけ）。mode＝「相違 なし／表示／変更行のみ」、counts＝件数のバッジ（畳んだときは出さない） */
export type RoundMarks = { mode: ReactNode; counts?: ReactNode };

/** 右のブロック（幅は中身で決まる＝左のチップの数では変わらない）：1段目＝相違・比較・⌃、2段目＝件数のバッジ */
function RightBlock({ m, collapsed, compare, toggle }: { m: RoundMarks | undefined; collapsed: boolean; compare: ReactNode; toggle: ReactNode }) {
  return (
    <div className={"rright" + (m ? " is-marks" : "")} data-testid="round-right">
      <div className="rrow">
        {m?.mode}
        {compare}
        {toggle}
      </div>
      {m?.counts && !collapsed ? <div className="rrow rcounts">{m.counts}</div> : null}
    </div>
  );
}

/**
 * 左のチップの列（1段・横に流す）。開いたとき・回が増えたとき・広げたときは表示中の回（下書き → 発行済 → 最後）が見える位置へ
 * （scrollIntoView と同じ。ページは縦に動かさない＝この列の scrollLeft だけ変える）。回が3つ以上なら左右に ‹ ›（端では押せない）
 */
function Chips({ count, focusNo, children }: { count: number; focusNo: number; children: ReactNode }) {
  const sc = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ start: true, end: true });
  const arrows = count >= 3;
  useLayoutEffect(() => {
    const el = sc.current;
    if (!el) return;
    const chip = el.querySelector<HTMLElement>(`[data-round="${focusNo}"]`);
    if (chip) {
      const c = chip.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      if (c.right > b.right) el.scrollLeft += c.right - b.right + 8;
      if (c.left < b.left) el.scrollLeft -= b.left - c.left + 8;
    }
    const upd = () => setEdge((p) => {
      const start = el.scrollLeft <= 1;
      const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      return p.start === start && p.end === end ? p : { start, end };
    });
    upd();
    el.addEventListener("scroll", upd, { passive: true });
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(upd);
    ro?.observe(el);
    return () => {
      el.removeEventListener("scroll", upd);
      ro?.disconnect();
    };
  }, [count, focusNo]);
  const step = (d: -1 | 1) => {
    const el = sc.current;
    if (el) el.scrollBy({ left: d * Math.max(120, el.clientWidth - 64), behavior: "smooth" });
  };
  return (
    <div className={"rleft" + (arrows ? " has-arrows" : "")}>
      {arrows ? (
        <button type="button" className="rscroll" aria-label={ROUND_MSG.scrollPrev} title={ROUND_MSG.scrollPrev} disabled={edge.start} onClick={() => step(-1)} data-testid="rounds-prev">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
        </button>
      ) : null}
      <div className="rrounds" ref={sc} data-testid="round-chips">
        {children}
      </div>
      {arrows ? (
        <button type="button" className="rscroll" aria-label={ROUND_MSG.scrollNext} title={ROUND_MSG.scrollNext} disabled={edge.end} onClick={() => step(1)} data-testid="rounds-next">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
        </button>
      ) : null}
    </div>
  );
}

/** 表示中の回：下書き（訂正中）→ 発行済 → 最後 */
const focusOf = (states: Array<{ no: number; state: RoundState }>): number =>
  (states.find((r) => r.state === "draft") ?? states.find((r) => r.state === "current") ?? states[states.length - 1])?.no ?? 1;

export function RoundStrip({ estimateId, expected, marks, doc = "estimates", label = ROUND_MSG.strip, previousExtra }: {
  estimateId: string;
  /** 道の頭（見積書＝estimates／{order}＝orders。#233 2b） */
  doc?: "estimates" | "orders";
  /** 帯の名前（読み上げ。{order}は「この{order}の回」） */
  label?: string;
  /** 前の回のチップに足す操作（{order}の承諾の記録＝{orderAck}・請書の写し） */
  previousExtra?: ((r: RevisionRound) => ReactNode) | undefined;
  /** 開いている回（見積の revision.no）と直している最中か。届くまでの代わりの形に使う（#179） */
  expected?: { no: number; revising: boolean };
  /** 訂正中だけ：相違（なし／表示／変更行のみ）と件数のバッジ（#190。畳んだときは件数を出さない） */
  marks?: ((collapsed: boolean) => RoundMarks) | undefined;
}) {
  const q = useQuery({ queryKey: [doc === "orders" ? "order-rounds" : "estimate-rounds", estimateId], queryFn: () => api<RevisionRounds>("GET", `/${doc}/${estimateId}/revisions`), retry: false });
  const rounds = q.data?.rounds ?? [];
  const [collapsed, setCollapsed] = useCollapsed();
  // 控えの PDF：押した瞬間に新しいタブで「PDF 作成中…」、作成中は押せない（docs/72 §10。控えは作ったあと保存される）
  const tab = usePdfTab();
  // 届くまでは、同じ数の回を同じ形（前の回は「控え PDF」の段つき）で出しておく（#179：帯が後から入ると、下の明細の表が帯の高さぶんずれて押し間違える）
  if (q.isPending && expected && expected.no >= 2) return <RoundStripLoading {...expected} collapsed={collapsed} marks={marks} label={label} />;
  if (rounds.length < 2) return null;
  const last = rounds[rounds.length - 1]!.no;
  const m = marks?.(collapsed);
  const right = (
    <RightBlock
      m={m}
      collapsed={collapsed}
      compare={<Link className="btn rcmp" to={`/${doc}/${estimateId}/compare?from=${last - 1}&to=${last}`} data-testid="go-compare">{ROUND_MSG.compare}</Link>}
      toggle={<Toggle collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />}
    />
  );
  const toCompare = (r: RevisionRound) => `/${doc}/${estimateId}/compare?from=${r.no === 1 ? 1 : r.no - 1}&to=${r.no === 1 ? 2 : r.no}`;
  const copyUrl = (no: number) => `/api/v1/${doc}/${estimateId}/pdf?rev=${no}`;
  if (collapsed) {
    return (
      <div className="paper rstrip is-collapsed" role="group" data-testid="round-strip" aria-label={label}>
        <span className="rline">
          {rounds.map((r, i) => (
            <span key={r.no} className="rtext" data-testid={`round-${r.no}`}>
              {i > 0 ? <span className="rarrow" aria-hidden="true">→</span> : null}
              <Link to={toCompare(r)} title={ROUND_MSG.compareTitle}>
                {r.no}回目 {r.state === "draft" ? ROUND_STATE_LABEL.draft : mdSlash(r.issueDate)} <span className="num">{yen(r.total)}円</span>
                <DeltaText d={r.deltaFromPrev} />
              </Link>
            </span>
          ))}
        </span>
        {right}
      </div>
    );
  }
  return (
    <div className="paper rstrip" role="group" data-testid="round-strip" aria-label={label}>
      <Chips count={rounds.length} focusNo={focusOf(rounds)}>
        {rounds.map((r, i) => (
          <span className="rwrap" key={r.no}>
            {i > 0 ? <span className="rarrow" aria-hidden="true">→</span> : null}
            <span className={"rchip is-" + r.state} data-testid={`round-${r.no}`} data-round={r.no}>
              <Link className="rmain" to={toCompare(r)} title={ROUND_MSG.compareTitle}>
                <b>
                  {r.no}回目 <StateChip state={r.state} />
                </b>
                <span className="rsub">
                  {r.state === "draft" ? "" : mdSlash(r.issueDate) + "　"}
                  <span className="num">{yen(r.total)}円</span>
                  <DeltaText d={r.deltaFromPrev} />
                </span>
              </Link>
              {r.state === "previous" ? (
                <button
                  type="button"
                  className="btn text rcopy"
                  disabled={tab.busy != null}
                  aria-busy={tab.busy === copyUrl(r.no)}
                  onClick={() => tab.open(copyUrl(r.no), { stored: true })}
                  data-testid={`round-copy-${r.no}`}
                >
                  {tab.busy === copyUrl(r.no) ? MSG.pdfMaking : ROUND_MSG.copyPdf}
                </button>
              ) : null}
              {r.state === "previous" && previousExtra ? previousExtra(r) : null}
            </span>
          </span>
        ))}
      </Chips>
      {right}
    </div>
  );
}

function StateChip({ state }: { state: RoundState }) {
  return <span className={"chip" + (state === "current" ? " ai" : "")}>{ROUND_STATE_LABEL[state]}</span>;
}

/** 回の帯を待つ間の代わり（同じ数の回・同じ形・同じ高さ。押せない）。前の回＝直している最中なら その前の回より前、出した回を見ているならそれより前 */
function RoundStripLoading({ no, revising, collapsed, marks, label }: { no: number; revising: boolean; collapsed: boolean; marks?: ((collapsed: boolean) => RoundMarks) | undefined; label: string }) {
  const lastPrev = revising ? no - 2 : no - 1;
  const stateOf = (n: number): RoundState => (n <= lastPrev ? "previous" : n === no && revising ? "draft" : "current");
  const states = Array.from({ length: no }, (_, i) => ({ no: i + 1, state: stateOf(i + 1) }));
  const right = (
    <RightBlock
      m={marks?.(collapsed)}
      collapsed={collapsed}
      compare={<span className="btn rcmp" aria-hidden="true">{ROUND_MSG.compare}</span>}
      toggle={<span className="rtoggle" aria-hidden="true" />}
    />
  );
  if (collapsed) {
    return (
      <div className="paper rstrip is-collapsed" role="group" aria-busy="true" aria-label={label} data-testid="round-strip-loading">
        <span className="rline">
          {states.map(({ no: n, state }) => (
            <span key={n} className="rtext">
              {n > 1 ? <span className="rarrow" aria-hidden="true">→</span> : null}
              {n}回目 {state === "draft" ? ROUND_STATE_LABEL.draft : "…"}
            </span>
          ))}
        </span>
        {right}
      </div>
    );
  }
  return (
    <div className="paper rstrip" role="group" aria-busy="true" aria-label={label} data-testid="round-strip-loading">
      <Chips count={no} focusNo={focusOf(states)}>
        {states.map(({ no: n, state }) => (
          <span className="rwrap" key={n}>
            {n > 1 ? <span className="rarrow" aria-hidden="true">→</span> : null}
            <span className={"rchip is-" + state} data-round={n}>
              <span className="rmain">
                <b>
                  {n}回目 <StateChip state={state} />
                </b>
                <span className="rsub">…</span>
              </span>
              {state === "previous" ? <span className="btn text rcopy" aria-hidden="true">{ROUND_MSG.copyPdf}</span> : null}
            </span>
          </span>
        ))}
      </Chips>
      {right}
    </div>
  );
}
